// Text-file helpers for scanner and master files.
//
// Every value is kept as a string. EAN, Article and Location codes are never
// converted to numbers, so leading zeros ("0089012...") survive import,
// storage, review and Excel export. Values are only trimmed.

/** Removes a UTF-8 byte-order mark and normalises line endings. */
export function cleanText(text) {
  let value = String(text ?? '');
  if (value.charCodeAt(0) === 0xfeff) value = value.slice(1);
  return value;
}

export function trimCell(value) {
  return value == null ? '' : String(value).trim();
}

const CANDIDATE_DELIMITERS = ['\t', ',', '|', ';'];

/**
 * Picks the delimiter that splits the header line into the most columns.
 * Returns '\t' for single-column files.
 */
export function detectDelimiter(firstLine) {
  let best = '\t';
  let bestCount = 1;
  for (const delimiter of CANDIDATE_DELIMITERS) {
    const count = splitLine(firstLine, delimiter).length;
    if (count > bestCount) {
      best = delimiter;
      bestCount = count;
    }
  }
  return best;
}

/** Splits one line, honouring double quotes ("a,b" stays one cell; "" is a quote). */
export function splitLine(line, delimiter) {
  if (line.indexOf('"') === -1) return line.split(delimiter);

  const cells = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cell += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        cell += ch;
      }
    } else if (ch === '"' && cell.trim() === '') {
      inQuotes = true;
      cell = '';
    } else if (ch === delimiter) {
      cells.push(cell);
      cell = '';
    } else {
      cell += ch;
    }
  }
  cells.push(cell);
  return cells;
}

/**
 * Iterates the lines of a text file without building one giant array of
 * cells. Yields { lineNumber (1-based), cells }. Blank lines are skipped.
 */
export function* iterateRows(text, delimiter) {
  const source = cleanText(text);
  let start = 0;
  let lineNumber = 0;
  const length = source.length;

  while (start <= length) {
    let end = source.indexOf('\n', start);
    if (end === -1) end = length;
    let line = source.slice(start, end);
    if (line.endsWith('\r')) line = line.slice(0, -1);
    lineNumber += 1;
    start = end + 1;

    if (line.trim() !== '') {
      yield { lineNumber, cells: splitLine(line, delimiter).map(trimCell) };
    }
    if (end === length) break;
  }
}

/** First non-blank line of a file (used for delimiter + header detection). */
export function firstLine(text) {
  const source = cleanText(text);
  for (const line of source.split('\n', 50)) {
    const trimmed = line.replace(/\r$/, '');
    if (trimmed.trim() !== '') return trimmed;
  }
  return '';
}

/** Parses a whole small file (masters, lists) into { delimiter, rows }. */
export function parseTable(text) {
  const header = firstLine(text);
  const delimiter = detectDelimiter(header);
  const rows = [];
  for (const row of iterateRows(text, delimiter)) rows.push(row);
  return { delimiter, rows };
}

/** Lower-case, collapse spaces/underscores/dots, for header matching. */
export function normaliseHeader(value) {
  return trimCell(value)
    .toLowerCase()
    .replace(/[._\-/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parses a quantity. Returns { value, ok }. Blank -> { value: 0, ok: false }.
 * Accepts "1", "1.5", "1,234" (thousands comma when no decimal comma).
 */
export function parseQuantity(raw) {
  const text = trimCell(raw);
  if (text === '') return { value: 0, ok: false };
  const cleaned = text.replace(/,(?=\d{3}(\D|$))/g, '');
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return { value: 0, ok: false };
  return { value, ok: true };
}

/** Parses a money/rate value. Blank or non-numeric -> null (never zero). */
export function parseRate(raw) {
  const text = trimCell(raw).replace(/[₹$,\s]/g, '');
  if (text === '') return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}

/** Small, fast, stable 53-bit string hash (cyrb53). */
export function hashString(text, seed = 0) {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

export function roundQty(value) {
  return Math.round(Number(value) * 1000) / 1000;
}

export function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

/** Yields to the browser so long imports keep the page responsive. */
export function yieldToUi() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

let idCounter = 0;
export function newId(prefix = 'id') {
  idCounter = (idCounter + 1) % 1e6;
  return `${prefix}_${Date.now().toString(36)}${idCounter.toString(36)}${Math.random()
    .toString(36)
    .slice(2, 6)}`;
}
